import { clamp } from "lodash";

/**
 * Translate a horizontal drag on the "add cluster" bar into a week span.
 *
 * The drag is anchored at the clicked week. Dragging right extends the span
 * forward, dragging left extends it backwards. The result is clamped to the
 * available weeks. A drag that stays within the same column yields a single
 * week (a plain click).
 *
 * @param {object} params
 * @param {number} params.startWeekIndex - week index the drag started on
 * @param {number} params.deltaX - horizontal pixels moved since mousedown
 * @param {number} params.columnWidth - width of one week column in pixels
 * @param {number} params.totalWeeks - total number of week columns
 * @returns {{ startIndex: number, duration: number }}
 */
export const getDragCreateSpan = ({
  startWeekIndex,
  deltaX,
  columnWidth,
  totalWeeks,
}) => {
  const changeInColumns = Math.round(deltaX / columnWidth);

  const rawStart =
    changeInColumns < 0 ? startWeekIndex + changeInColumns : startWeekIndex;
  const rawEnd =
    changeInColumns < 0 ? startWeekIndex : startWeekIndex + changeInColumns;

  const startIndex = clamp(rawStart, 0, totalWeeks - 1);
  const endIndex = clamp(rawEnd, 0, totalWeeks - 1);

  return {
    startIndex,
    duration: endIndex - startIndex + 1,
  };
};

/**
 * Week range occupied by a grid row element. Clusters carry the full span in
 * `planningMeta.weekIndexes`; empty cells only carry a single `weekIndex`.
 */
export const weekSpanOf = (element) => {
  if (element?.planningMeta?.weekIndexes?.length) {
    return element.planningMeta.weekIndexes;
  }

  if (element?.weekIndex != null) {
    return [element.weekIndex];
  }

  return [];
};

/**
 * Array index of the row element occupying `week`. Works whether that week is
 * an empty cell or covered by a multi-week cluster (a single array slot). This
 * is why a plain `findIndex(el => el.weekIndex === week)` fails: cluster slots
 * have no `weekIndex`, so a week covered by a cluster is never matched.
 */
export const findRowInsertIndex = (row, week) =>
  row.findIndex((element) => weekSpanOf(element).includes(week));

/**
 * Cluster elements in a row whose week span overlaps `[startWeek, endWeek]`.
 * Compared in week space (not array index), so multi-week clusters preceding
 * the insertion point don't skew the overlap window.
 */
export const getRowConflicts = (row, startWeek, duration) => {
  const endWeek = startWeek + duration - 1;

  return row.filter((element) => {
    if (!element?.planningMeta?.weekIndexes?.length) {
      return false;
    }

    const weeks = weekSpanOf(element);
    const elementStart = weeks[0];
    const elementEnd = weeks[weeks.length - 1];

    return elementStart <= endWeek && elementEnd >= startWeek;
  });
};
